import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Typography
} from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import BasicLayout from "components/Layouts/BasicLayout";
import LoadingOverlay from "components/LoadingOverlay";
import ticketReservationsService from "services/ticketReservations";
import { ITicketReservation } from "types/ticketReservations";
import { format } from "date-fns";

const TicketReservation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [reservation, setReservation] = useState<ITicketReservation | null>(null);

  useEffect(() => {
    const fetchReservation = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await ticketReservationsService.fetch(id);
        setReservation(data);
      } catch (error) {
        console.error("Error fetching ticket reservation:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReservation();
  }, [id]);

  const handleComplete = async () => {
    if (!id) return;

    try {
      setLoading(true);
      await ticketReservationsService.update(id, {
        status: 'completed'
      });

      // Refresh the data
      const updatedData = await ticketReservationsService.fetch(id);
      alert('Reservation completed');
      setReservation(updatedData);
    } catch (error) {
      console.error("Error completing reservation:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <BasicLayout>
        <LoadingOverlay />
      </BasicLayout>
    );
  }

  if (!reservation) {
    return (
      <BasicLayout>
        <Box>
          <IconButton onClick={() => navigate("/ticket-reservations")}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h4">Ticket Reservation Not Found</Typography>
        </Box>
      </BasicLayout>
    );
  }

  return (
    <BasicLayout>
      <Box mb={3}>
        <IconButton onClick={() => navigate("/ticket-reservations")}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" component="span" ml={1}>
          Ticket Reservation Details
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Reservation ID
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reservation.id}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Status
              </Typography>
              <Chip
                label={reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}
                color={getStatusColor(reservation.status)}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Event
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reservation?.event?.title}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                User
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reservation.user}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Organization
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reservation.organization?.name}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" color="textSecondary">
                Expires At
              </Typography>
              <Typography variant="body1" gutterBottom>
                {reservation.expiresAt
                  ? format(new Date(reservation.expiresAt), 'MMM dd, yyyy h:mm a')
                  : 'N/A'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Tickets
              </Typography>

              {reservation.tickets.map((ticket, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" color="textSecondary">
                          Ticket Type
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {ticket.ticketType.name}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" color="textSecondary">
                          Quantity
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {ticket.quantity}
                        </Typography>
                      </Grid>

                      {ticket.selectedOptions && ticket.selectedOptions.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" color="textSecondary">
                            Selected Options
                          </Typography>
                          {ticket.selectedOptions.map((option, optionIndex) => (
                            <Box key={optionIndex} mt={1}>
                              <Typography variant="body2" component="span" fontWeight="bold">
                                {option.ticketTypeOption.name}:
                              </Typography>{" "}
                              <Typography variant="body2" component="span">
                                {String(option.value)}
                              </Typography>
                            </Box>
                          ))}
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                {reservation.status === 'active' && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleComplete}
                  >
                    Complete Reservation
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </BasicLayout>
  );
};

export default TicketReservation; 